import {
    Group,
    Stack,
    Text,
    Title,
} from '@mantine/core';

import {PhaseStatusBadge} from './PhaseStatusBadge.tsx';
import {StepBlock} from './StepBlock.tsx';
import {ExecutionPhase} from '../types.ts';
import {formatTimestampText} from '../../../utils/TableTimestamps.tsx';


// Extracted from executions/pages/EditPage.tsx during FNP-79 so the
// applications detail page can render the same phase UI.
//
// ⚠️ FNP-1029: this builds a STRING, so it must use `formatTimestampText`, not
// the JSX-returning `formatTimestamp`. Interpolating a React element into a
// template literal renders the literal text "[object Object]" — which is
// exactly what every phase row showed in test:
//
//     pending → [object Object]
//
// `tsc` cannot catch it (a template literal accepts anything), and the
// both-absent case returns early without calling the formatter at all, so a
// test that only covers `n/a` passes while the real path is broken.
const formatTimeRange = (startedAt?: string, completedAt?: string) => {
    if (!startedAt && !completedAt) {
        return 'n/a';
    }
    const start = startedAt ? formatTimestampText(startedAt) : 'pending';
    const end = completedAt ? formatTimestampText(completedAt) : 'in progress';
    return `${start} → ${end}`;
};


export const PhaseBlock = ({phase}: { phase: ExecutionPhase }) => {
    const steps = (phase.steps ?? []).slice().sort(
        (a, b) => (a.position ?? 0) - (b.position ?? 0),
    );
    return (
        <Stack gap={'sm'}>
            <Group justify={'space-between'} wrap={'nowrap'}>
                <Group gap={'xs'} wrap={'nowrap'}>
                    <Title order={5}>{phase.position}. {phase.phaseName ?? phase.id}</Title>
                    <PhaseStatusBadge status={phase.phaseStatus}/>
                </Group>
                <Text c={'dimmed'} size={'xs'}>
                    {formatTimeRange(phase.startedAt, phase.completedAt)}
                </Text>
            </Group>

            <Stack gap={'md'} pl={'md'}>
                {steps.length === 0 ? (
                    <Text size={'sm'} c={'dimmed'}>No steps recorded for this phase.</Text>
                ) : (
                    steps.map((step) => <StepBlock key={step.id} step={step}/>)
                )}
            </Stack>
        </Stack>
    );
};
